import React from "react";
import ReactDOM from "react-dom/client";
import { OpenFeature } from '@openfeature/web-sdk';
import { OpenFeatureProvider } from '@openfeature/react-sdk';
import App from "./App";
import "./index.css";
import "./tours/tour-styles.css";
import { BackendSyncProvider } from './providers/BackendSyncProvider';
import { captureError, initSentry } from './lib/sentry';
import { ErrorBoundary } from './components/errors';
import ServerError from './pages/ServerError';

// Recover from stale lazy-chunk imports after a deploy. Each deploy replaces
// the hashed chunks in the served dist, so an already-open tab requests old
// chunk URLs that now 404. Reload once to fetch the current index.html and
// fresh chunk hashes; the timestamp guard prevents a reload loop when the
// failure is not deploy-related.
// A stale CDN can keep serving an index.html whose chunks 404 — reloading
// unconditionally then loops (load → 404 → reload → same stale HTML…). Allow
// two reload attempts per 5-minute window; past that, let the failed import
// surface to the error boundary instead of looping.
window.addEventListener('vite:preloadError', (event: Event) => {
  const KEY = 'vite:preloadError:reloads';
  let state = { count: 0, last: 0 };
  try {
    state = JSON.parse(sessionStorage.getItem(KEY) || '') as typeof state;
  } catch {
    // first hit or unparsable — keep zero state
  }
  if (Date.now() - state.last > 5 * 60_000) {
    state.count = 0;
  }
  // Telemetry: silent reloads made this class of failure invisible — a
  // reported flicker-loop could never be confirmed. Every preload failure now
  // lands in Sentry with the failing chunk and the attempt count.
  const payload = (event as unknown as { payload?: unknown }).payload;
  captureError(new Error('vite:preloadError'), {
    chunk: payload instanceof Error ? payload.message : String(payload ?? ''),
    reloadCount: state.count,
    url: window.location.href,
    action: state.count >= 2 ? 'gave-up' : 'reloading',
  });
  if (state.count >= 2) {
    return;
  }
  event.preventDefault();
  sessionStorage.setItem(
    KEY,
    JSON.stringify({ count: state.count + 1, last: Date.now() }),
  );
  window.location.reload();
});

// Initialize Sentry before anything else
initSentry();

// Initialize OpenFeature with our custom backend sync provider
const provider = new BackendSyncProvider();
OpenFeature.setProvider(provider);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <ServerError error={error} resetErrorBoundary={resetErrorBoundary} />
      )}
    >
      <OpenFeatureProvider>
        <App />
      </OpenFeatureProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
